import * as Dialog from "@radix-ui/react-dialog";
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  RotateCw,
  Video,
  X,
} from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOwnerIdentity } from "@/hooks/useOwnerIdentity";
import {
  OWNER_DEMO_FALLBACK_URL,
  OwnerDemoApiError,
  type OwnerDemoBooking,
  useBookDemo,
  useDemoAvailability,
  useOwnerDemoStatus,
} from "@/hooks/owner/useOwnerDemo";
import { cn } from "@/lib/utils";

const BERLIN_TIMEZONE = "Europe/Berlin";
const DISMISS_KEY = "coliviq.ownerDemo.pulseDismissedUntil";
const DAY_MS = 24 * 60 * 60 * 1000;

type Step = "pick" | "details" | "done";

function dateKey(value: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BERLIN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function calendarKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function replaceValue(template: string, value: string): string {
  return template.replace("{email}", value).replace("{date}", value);
}

function readPulseDismissed(): boolean {
  try {
    return Number(localStorage.getItem(DISMISS_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

function writePulseDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * DAY_MS));
  } catch {
    // localStorage can be unavailable in private browsing. The booking flow
    // must still work; only the cosmetic pulse suppression is lost.
  }
}

export function OwnerDemoCta() {
  const location = useLocation();
  const { isOwnerPortalOnly, isImpersonating, hasOperatorRole, rolesReady } =
    useAuth();
  const { language, t } = useLanguage();
  const identity = useOwnerIdentity(false);
  const ownerRoute =
    location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  // The add-property wizard is a focused full-page flow with its own sticky
  // bottom-right Back/Continue footer; the floating bubble sits on top of it.
  const onPropertyWizard = location.pathname === "/admin/properties/new";
  const visible =
    rolesReady &&
    isOwnerPortalOnly &&
    !hasOperatorRole &&
    ownerRoute &&
    !onPropertyWizard;

  const range = useMemo(() => {
    const start = new Date();
    start.setSeconds(0, 0);
    const end = new Date(start.getTime() + 30 * DAY_MS);
    return { start, end };
  }, []);
  const rangeStart = range.start.toISOString();
  const rangeEnd = range.end.toISOString();

  const [open, setOpen] = useState(false);
  const status = useOwnerDemoStatus(visible, open);
  const booking = status.data?.booking ?? null;
  const [step, setStep] = useState<Step>("pick");
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState<
    "slot_taken" | "already_booked" | "booking_error" | null
  >(null);
  const [pulseDismissed, setPulseDismissed] = useState(readPulseDismissed);
  const identitySeeded = useRef(false);

  useEffect(() => {
    if (identitySeeded.current) return;
    if (identity.displayName || identity.email) {
      setName(identity.displayName);
      setEmail(identity.email);
      identitySeeded.current = true;
    }
  }, [identity.displayName, identity.email]);

  useEffect(() => {
    // A booking that disappears server-side (cancelled on Calendly) has to
    // take the panel back to the picker, even while the dialog is open.
    if (booking) setStep("done");
    else setStep((current) => (current === "done" ? "pick" : current));
  }, [booking]);

  const availability = useDemoAvailability(
    rangeStart,
    rangeEnd,
    visible && open && !booking
  );
  const bookDemo = useBookDemo();

  const slotsByDay = useMemo(() => {
    const grouped = new Map<string, string[]>();
    for (const slot of availability.data?.slots ?? []) {
      const parsed = new Date(slot.startTime);
      if (!Number.isFinite(parsed.getTime())) continue;
      const key = dateKey(parsed);
      const values = grouped.get(key) ?? [];
      values.push(slot.startTime);
      grouped.set(key, values);
    }
    for (const values of grouped.values()) values.sort();
    return grouped;
  }, [availability.data?.slots]);

  const locale = language === "de" ? "de-DE" : "en-GB";
  const monthBase = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);
  const month = new Date(
    monthBase.getFullYear(),
    monthBase.getMonth() + monthOffset,
    1
  );
  const maxMonthOffset =
    range.end.getFullYear() * 12 +
    range.end.getMonth() -
    (monthBase.getFullYear() * 12 + monthBase.getMonth());
  const firstWeekday = (month.getDay() + 6) % 7;
  const dayCount = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0
  ).getDate();
  const calendarDays = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: dayCount }, (_, index) => index + 1),
  ];
  const selectedSlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : [];
  const selectedDate = selectedStart ? new Date(selectedStart) : null;

  const longDate = (value: Date) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: BERLIN_TIMEZONE,
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(value);
  const shortDateTime = (value: Date) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: BERLIN_TIMEZONE,
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(value);
  const time = (value: Date) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: BERLIN_TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(value);
  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(month);

  const fallbackUrl =
    status.data?.fallbackUrl ??
    (availability.error instanceof OwnerDemoApiError
      ? availability.error.fallbackUrl
      : undefined) ??
    OWNER_DEMO_FALLBACK_URL;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setStep(booking ? "done" : step === "done" ? "pick" : step);
      posthog.capture("demo_bubble_opened");
      return;
    }
    if (!booking) {
      writePulseDismissed();
      setPulseDismissed(true);
    }
  };

  const selectSlot = (startTime: string) => {
    setSelectedStart(startTime);
    setMessage(null);
    posthog.capture("demo_slot_selected", { start_time: startTime });
  };

  const submit = async () => {
    // Impersonation is a support preview. Never let an administrator create a
    // real Calendly invitee or mutate the owner's server-owned demo state.
    if (isImpersonating) return;
    if (!selectedStart) return;
    if (
      name.trim().length < 2 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    ) {
      setMessage("booking_error");
      return;
    }
    setMessage(null);
    try {
      await bookDemo.mutateAsync({
        startTime: selectedStart,
        name: name.trim(),
        email: email.trim(),
        note: note.trim(),
      });
      setStep("done");
      posthog.capture("demo_booked", { start_time: selectedStart });
    } catch (error) {
      const conflict =
        error instanceof OwnerDemoApiError && error.reason === "slot_taken";
      const alreadyBooked =
        error instanceof OwnerDemoApiError && error.reason === "already_booked";
      setMessage(
        conflict
          ? "slot_taken"
          : alreadyBooked
            ? "already_booked"
            : "booking_error"
      );
      posthog.capture("demo_error", {
        stage: "book",
        reason: conflict
          ? "slot_taken"
          : alreadyBooked
            ? "already_booked"
            : "request_failed",
      });
      if (conflict) {
        setStep("pick");
        setSelectedStart(null);
        void availability.refetch();
      } else if (alreadyBooked) {
        // Another tab or request may have completed first. Refresh the
        // server-owned status so an active booking moves straight to "done";
        // otherwise keep a clear non-retry message while the webhook catches up.
        void status.refetch();
      }
    }
  };

  if (!visible) return null;

  // The server status is the only source here. useBookDemo already writes its
  // result into that query's cache, so the mutation result added nothing but a
  // copy that never cleared - it outlived the booking and kept the bubble
  // advertising an appointment the owner had already cancelled.
  const shownBooking: OwnerDemoBooking | null = booking;
  const doneDate = shownBooking
    ? new Date(shownBooking.startTime)
    : selectedDate;
  const bubbleLabel = shownBooking
    ? replaceValue(
        t("owner.demo.appointment", "Demo on {date}"),
        shortDateTime(new Date(shownBooking.startTime))
      )
    : t("owner.demo.bubble", "Book a demo");

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[49] bg-transparent" />
        <Dialog.Content
          aria-describedby="owner-demo-description"
          className="fixed bottom-0 right-0 z-50 flex max-h-[calc(100dvh-16px)] w-full flex-col overflow-hidden rounded-t-[22px] border border-[#ECE7DD] bg-white text-[#1A2B2A] shadow-[0_24px_60px_-12px_rgba(15,76,74,.30),0_4px_14px_rgba(15,76,74,.08)] outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 md:bottom-[104px] md:right-7 md:max-h-[calc(100dvh-132px)] md:w-[372px] md:rounded-[22px] md:data-[state=open]:fade-in md:data-[state=open]:zoom-in-95"
        >
          <div className="flex flex-none items-start gap-3 bg-[#0F4C4A] px-5 pb-4 pt-[18px] text-white">
            <span className="min-w-0 flex-1">
              <Dialog.Title className="block text-[15px] font-semibold leading-5 tracking-[-.01em]">
                {t("owner.demo.title", "Book a demo call")}
              </Dialog.Title>
              <Dialog.Description
                id="owner-demo-description"
                className="mt-[3px] block text-xs text-[#9FBDBA]"
              >
                {t("owner.demo.meta", "30 minutes · video call · free")}
              </Dialog.Description>
            </span>
            <Dialog.Close
              aria-label={t("owner.demo.close", "Close")}
              className="inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ECDC4]"
            >
              <X className="h-[15px] w-[15px]" strokeWidth={2.5} />
            </Dialog.Close>
          </div>

          <div className="min-h-0 overflow-y-auto">
            {isImpersonating && (
              <div
                role="status"
                className="mx-5 mt-[18px] rounded-[14px] border border-[#CDE5E2] bg-[#F1FAF9] px-4 py-3 text-[12.5px] leading-5 text-[#344746]"
              >
                {t(
                  "owner.demo.previewOnly",
                  "Preview only — booking is disabled while acting as an owner."
                )}
              </div>
            )}
            {step === "pick" && !shownBooking && (
              <div className="px-5 pb-4 pt-[18px]">
                <div className="mb-[10px] flex items-center justify-between">
                  <span className="text-sm font-semibold capitalize text-[#0F4C4A]">
                    {monthLabel}
                  </span>
                  <span className="flex gap-1">
                    <button
                      type="button"
                      aria-label={t(
                        "owner.demo.previousMonth",
                        "Previous month"
                      )}
                      disabled={monthOffset === 0}
                      onClick={() =>
                        setMonthOffset((value) => Math.max(0, value - 1))
                      }
                      className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[#ECE7DD] bg-white text-[#344746] disabled:cursor-default disabled:opacity-35 md:h-7 md:w-7"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      aria-label={t("owner.demo.nextMonth", "Next month")}
                      disabled={monthOffset >= maxMonthOffset}
                      onClick={() =>
                        setMonthOffset((value) =>
                          Math.min(maxMonthOffset, value + 1)
                        )
                      }
                      className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-[#ECE7DD] bg-white text-[#344746] disabled:cursor-default disabled:opacity-35 md:h-7 md:w-7"
                    >
                      <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  </span>
                </div>

                <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] font-semibold uppercase tracking-[.1em] text-[#7A8E8C]">
                  {(language === "de"
                    ? ["MO", "DI", "MI", "DO", "FR", "SA", "SO"]
                    : ["MO", "TU", "WE", "TH", "FR", "SA", "SU"]
                  ).map((day) => (
                    <span key={day}>{day}</span>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-0.5">
                  {calendarDays.map((day, index) => {
                    if (!day)
                      return (
                        <span key={`blank-${index}`} className="h-11 md:h-9" />
                      );
                    const key = calendarKey(
                      month.getFullYear(),
                      month.getMonth(),
                      day
                    );
                    const enabled = slotsByDay.has(key);
                    const selected = selectedDay === key;
                    const fullDate = new Intl.DateTimeFormat(locale, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }).format(
                      new Date(month.getFullYear(), month.getMonth(), day)
                    );
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-label={fullDate}
                        aria-pressed={selected}
                        disabled={!enabled}
                        onClick={() => {
                          setSelectedDay(key);
                          setSelectedStart(null);
                          setMessage(null);
                        }}
                        className={cn(
                          "h-11 rounded-[10px] border-0 text-[13px] transition-colors md:h-9",
                          selected
                            ? "bg-[#0F4C4A] font-semibold text-white"
                            : enabled
                              ? "bg-[#E3EFED] font-semibold text-[#0F4C4A] hover:bg-[#CDE5E2]"
                              : "cursor-default bg-transparent font-normal text-[#B6C2C0]"
                        )}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>

                {availability.isLoading && (
                  <div
                    className="mt-4 border-t border-[#ECE7DD] pt-3.5"
                    role="status"
                  >
                    <span className="sr-only">
                      {t("owner.demo.loading", "Loading available times…")}
                    </span>
                    <div className="mb-2.5 h-4 w-40 animate-pulse rounded bg-[#E6EBEA]" />
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 6 }, (_, index) => (
                        <div
                          key={index}
                          className="h-[44px] animate-pulse rounded-full bg-[#F1F3F2] md:h-[38px]"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {availability.isError && (
                  <div className="mt-4 rounded-[14px] border border-[#ECE7DD] bg-[#FAF8F4] p-4 text-center">
                    <p className="text-[12.5px] text-[#5C6E6C]">
                      {t(
                        "owner.demo.error",
                        "Available times could not be loaded."
                      )}
                    </p>
                    <div className="mt-3 flex justify-center gap-3 text-xs font-semibold">
                      <button
                        type="button"
                        onClick={() => availability.refetch()}
                        className="inline-flex min-h-11 items-center gap-1.5 text-[#2FB3AA]"
                      >
                        <RotateCw className="h-3.5 w-3.5" />
                        {t("owner.demo.retry", "Try again")}
                      </button>
                      {!isImpersonating && (
                        <a
                          href={fallbackUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-11 items-center gap-1.5 text-[#0F4C4A]"
                        >
                          {t("owner.demo.fallback", "Open Calendly")}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {!availability.isLoading &&
                  !availability.isError &&
                  availability.data &&
                  slotsByDay.size === 0 && (
                    <div className="mt-4 rounded-[14px] bg-[#FAF8F4] p-4 text-center text-[12.5px] text-[#5C6E6C]">
                      <p>
                        {t(
                          "owner.demo.noTimes",
                          "There are no free times in the next 30 days."
                        )}
                      </p>
                      {!isImpersonating && (
                        <a
                          href={fallbackUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex min-h-11 items-center gap-1.5 font-semibold text-[#0F4C4A]"
                        >
                          {t("owner.demo.fallback", "Open Calendly")}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}

                {!availability.isLoading &&
                  selectedDay &&
                  selectedSlots.length > 0 && (
                    <div className="mt-4 border-t border-[#ECE7DD] pt-3.5">
                      <div className="mb-2.5 flex items-baseline justify-between">
                        <span className="text-[13px] font-semibold text-[#1A2B2A]">
                          {longDate(new Date(selectedSlots[0]))}
                        </span>
                        <span className="text-[11px] text-[#7A8E8C]">
                          {t("owner.demo.timezone", "CET · Berlin")}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedSlots.map((slot) => {
                          const selected = selectedStart === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              aria-label={`${longDate(new Date(slot))}, ${time(new Date(slot))}`}
                              aria-pressed={selected}
                              onClick={() => selectSlot(slot)}
                              className={cn(
                                "h-11 rounded-full border-[1.5px] text-[13px] font-semibold md:h-[38px]",
                                selected
                                  ? "border-[#0F4C4A] bg-[#0F4C4A] text-white"
                                  : "border-[#CDE5E2] bg-white text-[#0F4C4A] hover:bg-[#F1FAF9]"
                              )}
                            >
                              {time(new Date(slot))}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {!availability.isLoading &&
                  !availability.isError &&
                  slotsByDay.size > 0 &&
                  !selectedDay && (
                    <p className="mt-3.5 text-center text-[12.5px] text-[#5C6E6C]">
                      {t(
                        "owner.demo.chooseDay",
                        "Choose a day to see available times."
                      )}
                    </p>
                  )}

                {message === "slot_taken" && (
                  <p
                    className="mt-3 text-center text-xs font-medium text-[#A33A2B]"
                    role="alert"
                  >
                    {t(
                      "owner.demo.slotTaken",
                      "That time was just booked. Please choose another."
                    )}
                  </p>
                )}

                <button
                  type="button"
                  disabled={!selectedStart}
                  onClick={() => setStep("details")}
                  className="mt-4 h-11 w-full rounded-full border-0 bg-[#0F4C4A] text-sm font-semibold text-white hover:bg-[#0A3635] disabled:cursor-not-allowed disabled:bg-[#E6EBEA] disabled:text-[#7A8E8C]"
                >
                  {t("owner.demo.continue", "Continue")}
                </button>
              </div>
            )}

            {step === "details" && !shownBooking && selectedDate && (
              <div className="px-5 pb-[18px] pt-[18px]">
                <button
                  type="button"
                  onClick={() => setStep("pick")}
                  className="mb-3 inline-flex min-h-11 items-center gap-1 p-0 text-[12.5px] font-semibold text-[#2FB3AA] md:min-h-0"
                >
                  <ChevronLeft
                    className="h-[13px] w-[13px]"
                    strokeWidth={2.5}
                  />
                  {t("owner.demo.changeTime", "Change time")}
                </button>
                <div className="mb-4 flex items-center gap-3 rounded-[14px] bg-[#E3EFED] px-3.5 py-3">
                  <CalendarDays
                    className="h-5 w-5 flex-none text-[#0F4C4A]"
                    strokeWidth={2.5}
                  />
                  <span>
                    <span className="block text-[13.5px] font-semibold text-[#0F4C4A]">
                      {longDate(selectedDate)}
                    </span>
                    <span className="block text-xs text-[#344746]">
                      {time(selectedDate)} · 30{" "}
                      {language === "de" ? "Minuten" : "minutes"}
                    </span>
                  </span>
                </div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[.1em] text-[#7A8E8C]">
                  {t("owner.demo.name", "Name")}
                </label>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                  className="mb-3 h-[44px] w-full rounded-xl border border-[#E6EBEA] px-3.5 text-sm text-[#1A2B2A] outline-none focus:border-[#2FB3AA]"
                />
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[.1em] text-[#7A8E8C]">
                  {t("owner.demo.email", "Email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  className="mb-3 h-[44px] w-full rounded-xl border border-[#E6EBEA] px-3.5 text-sm text-[#1A2B2A] outline-none focus:border-[#2FB3AA]"
                />
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[.1em] text-[#7A8E8C]">
                  {t(
                    "owner.demo.note",
                    "What would you like to discuss? (optional)"
                  )}
                </label>
                <textarea
                  rows={2}
                  value={note}
                  maxLength={2000}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={t(
                    "owner.demo.notePlaceholder",
                    "e.g. add a second property"
                  )}
                  className="w-full resize-none rounded-xl border border-[#E6EBEA] px-3.5 py-2.5 text-sm text-[#1A2B2A] outline-none placeholder:text-[#9AA7A6] focus:border-[#2FB3AA]"
                />
                {message === "booking_error" && (
                  <p
                    className="mt-2 text-xs font-medium text-[#A33A2B]"
                    role="alert"
                  >
                    {bookDemo.error
                      ? t(
                          "owner.demo.bookingError",
                          "The booking could not be completed."
                        )
                      : t(
                          "owner.demo.required",
                          "Please enter a valid name and email."
                        )}
                  </p>
                )}
                {message === "already_booked" && (
                  <p
                    className="mt-2 text-xs font-medium text-[#A33A2B]"
                    role="alert"
                  >
                    {t(
                      "owner.demo.alreadyBooked",
                      "A demo is already booked for this account."
                    )}
                  </p>
                )}
                <button
                  type="button"
                  onClick={submit}
                  disabled={isImpersonating || bookDemo.isPending}
                  className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-full border-0 bg-[#0F4C4A] text-sm font-semibold text-white hover:bg-[#0A3635] disabled:opacity-60"
                >
                  {bookDemo.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("owner.demo.confirm", "Confirm booking")}
                </button>
                {!isImpersonating &&
                  bookDemo.error &&
                  !(
                    bookDemo.error instanceof OwnerDemoApiError &&
                    bookDemo.error.reason === "already_booked"
                  ) && (
                    <a
                      href={fallbackUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex min-h-11 w-full items-center justify-center gap-1.5 text-xs font-semibold text-[#0F4C4A]"
                    >
                      {t("owner.demo.fallback", "Open Calendly")}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
              </div>
            )}

            {step === "done" && doneDate && (
              <div className="px-6 pb-6 pt-[30px] text-center">
                <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#4ECDC4] text-[#06201F]">
                  <Check className="h-[26px] w-[26px]" strokeWidth={2.5} />
                </span>
                <div className="mt-4 text-[17px] font-semibold tracking-[-.01em] text-[#0F4C4A]">
                  {t("owner.demo.done", "Booked.")}
                </div>
                <div className="mt-1.5 text-[13.5px] leading-5 text-[#344746]">
                  {longDate(doneDate)}, {time(doneDate)}
                  <br />
                  {replaceValue(
                    t(
                      "owner.demo.inviteSent",
                      "The invitation with the video link will be sent to {email}."
                    ),
                    shownBooking?.inviteeEmail || email || identity.email
                  )}
                </div>
                {!isImpersonating &&
                  shownBooking &&
                  (shownBooking.rescheduleUrl || shownBooking.cancelUrl) && (
                    <div className="mt-4 flex justify-center gap-4 text-xs font-semibold">
                      {shownBooking.rescheduleUrl && (
                        <a
                          href={shownBooking.rescheduleUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-11 items-center text-[#2FB3AA]"
                        >
                          {t("owner.demo.reschedule", "Reschedule")}
                        </a>
                      )}
                      {shownBooking.cancelUrl && (
                        <a
                          href={shownBooking.cancelUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-11 items-center text-[#5C6E6C]"
                        >
                          {t("owner.demo.cancel", "Cancel appointment")}
                        </a>
                      )}
                    </div>
                  )}
                <Dialog.Close className="mt-5 h-[44px] w-full rounded-full border-[1.5px] border-[#ECE7DD] bg-white text-sm font-semibold text-[#0F4C4A] hover:bg-[#F7F5F0]">
                  {t("owner.demo.close", "Close")}
                </Dialog.Close>
              </div>
            )}
          </div>

          <div className="flex flex-none justify-between border-t border-[#ECE7DD] bg-[#FAF8F4] px-5 py-2.5 text-[11px] text-[#7A8E8C]">
            <span>
              {t(
                "owner.demo.liveTimes",
                "Available times live from the calendar"
              )}
            </span>
            <span>{t("owner.demo.provider", "via Calendly")}</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>

      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label={t("owner.demo.title", "Book a demo call")}
          className="fixed bottom-[calc(28px+env(safe-area-inset-bottom))] right-7 z-50 flex items-center gap-3 rounded-full border-0 bg-[#0F4C4A] py-2 pl-2 pr-[22px] text-left text-white shadow-[0_12px_30px_-6px_rgba(15,76,74,.45),0_2px_6px_rgba(15,76,74,.2)] transition-colors hover:bg-[#0A3635] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ECDC4] focus-visible:ring-offset-2 max-[420px]:right-4"
        >
          <span
            className={cn(
              "inline-flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#4ECDC4] text-[#06201F]",
              !shownBooking && !pulseDismissed && "owner-demo-pulse"
            )}
          >
            <Video className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span>
            <span className="block max-w-[190px] truncate text-sm font-semibold leading-[1.2]">
              {bubbleLabel}
            </span>
            <span className="mt-0.5 block text-[11.5px] text-[#9FBDBA]">
              {shownBooking
                ? t("owner.demo.done", "Booked.")
                : t("owner.demo.bubbleSubline", "30 min · Introduction")}
            </span>
          </span>
        </button>
      </Dialog.Trigger>
    </Dialog.Root>
  );
}
